import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, ChevronDown, Globe, ArrowRight, Loader2, Search as SearchIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { publicAPI } from '@/lib/api';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import HeaderSearch from '@/components/HeaderSearch';
import { toast } from 'sonner';

const LOGO_URL = "/logo.png";

// Default content values
const DEFAULT_CONTENT = {
  brand: {
    companyName: 'SINGHANIA INTERNATIONAL LTD.',
    tagline: 'Est. 1995',
    copyrightText: 'Singhania International Ltd. All rights reserved.'
  },
  contact: {
    phone: '+91 9357681418',
    whatsapp: '919357681418',
    email: 'info@singhaniainternational.com',
    salesEmail: 'sales@singhaniainternational.com',
    address: 'C-25, Phase-1, Focal Point, Ludhiana - 141010, Punjab, India'
  },
  cta: {
    primaryButton: 'Request Quote'
  },
  footer: {
    companyDescription: 'Global leaders in industrial trading and precision manufacturing since 1995. Serving 30+ countries with ISO & IATF certified quality.',
    starExportHouse: 'Star Export House',
    quickLinksTitle: 'Quick Links',
    productsTitle: 'Our Products',
    contactTitle: 'Contact Us',
    whatsappButton: 'Chat on WhatsApp',
    certifications: ['ISO 9001:2015', 'ISO 14001:2015', 'ISO 45001:2018', 'IATF 16949:2016'],
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service'
  }
};

const Header = ({ siteContent }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const c = siteContent || DEFAULT_CONTENT;
  const phoneNumber = c.contact?.phone || DEFAULT_CONTENT.contact.phone;
  const companyName = c.brand?.companyName || DEFAULT_CONTENT.brand.companyName;
  const tagline = c.brand?.tagline || DEFAULT_CONTENT.brand.tagline;

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { label: 'Home', path: '/' },
    { label: 'About Us', path: '/about' },
    {
      label: 'Divisions',
      children: [
        { label: 'Trading Division', path: '/trading' },
        { label: 'Manufacturing Division', path: '/manufacturing' },
      ],
    },
    { label: 'Products', path: '/products' },
    {
      label: 'Resources',
      children: [
        { label: 'Blog & News', path: '/blog' },
        { label: 'Downloads', path: '/downloads' },
      ],
    },
    { label: 'Careers', path: '/careers' },
    { label: 'Contact', path: '/contact' },
  ];

  return (
    <>
      {/* Top Bar */}
      <div className="bg-[#0F172A] text-white py-2 text-sm hidden md:block">
        <div className="container-custom flex justify-between items-center">
          <div className="flex items-center gap-6">
            <a href={`tel:${phoneNumber}`} className="flex items-center gap-2 hover:text-[#D32F2F] transition-colors">
              <Phone className="w-4 h-4" />
              <span>{phoneNumber}</span>
            </a>
            <a href={`mailto:${c.contact?.email || DEFAULT_CONTENT.contact.email}`} className="flex items-center gap-2 hover:text-[#D32F2F] transition-colors">
              <Mail className="w-4 h-4" />
              <span>{c.contact?.email || DEFAULT_CONTENT.contact.email}</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Ludhiana, Punjab, India
            </span>
            <span className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Exports to 30+ Countries
            </span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="container-custom">
          <div className="flex items-center justify-between h-20">
            {/* Logo + Company Name (ALWAYS VISIBLE) */}
            <Link to="/" className="flex items-center gap-2.5 shrink-0">
              <img src={LOGO_URL} alt="Singhania International Ltd." className="h-16 w-auto shrink-0" width="64" height="64" />
              <div className="hidden sm:block">
                <span className="font-heading font-bold text-sm lg:text-lg text-[#0F172A] leading-tight block whitespace-nowrap">{companyName}</span>
                <span className="text-[10px] lg:text-xs text-slate-500 uppercase tracking-wider">{tagline}</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center">
              {navLinks.map((link) =>
                link.children ? (
                  <DropdownMenu key={link.label}>
                    <DropdownMenuTrigger className="flex items-center gap-1 px-3 py-2 text-[#0F172A] hover:text-[#0047AB] text-sm font-medium transition-colors whitespace-nowrap">
                      {link.label}
                      <ChevronDown className="w-3.5 h-3.5" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      {link.children.map((child) => (
                        <DropdownMenuItem key={child.path} onClick={() => navigate(child.path)}>
                          {child.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive(link.path)
                        ? 'text-[#0047AB] border-b-2 border-[#0047AB]'
                        : 'text-[#0F172A] hover:text-[#0047AB]'
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              )}
            </nav>

            {/* Language Switcher + Search + CTA Button */}
            <div className="hidden lg:flex items-center gap-2 shrink-0">
              {searchOpen ? (
                <div className="w-72 relative">
                  <HeaderSearch onClose={() => setSearchOpen(false)} />
                </div>
              ) : (
                <button
                  onClick={() => setSearchOpen(true)}
                  className="p-2 text-slate-500 hover:text-[#0047AB] transition-colors"
                  aria-label="Search products"
                  data-testid="desktop-search-toggle"
                >
                  <SearchIcon className="w-5 h-5" />
                </button>
              )}
              <LanguageSwitcher />
              <Link to="/rfq">
                <Button 
                  className="bg-[#D32F2F] hover:bg-[#B71C1C] text-white font-bold uppercase tracking-wide rounded-sm text-sm px-4"
                  data-testid="header-rfq-btn"
                >
                  {c.cta?.primaryButton || 'Request Quote'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button + Search + Language */}
            <div className="lg:hidden flex items-center gap-2">
              <button
                className="p-2 text-slate-500 hover:text-[#0047AB]"
                onClick={() => setSearchOpen(!searchOpen)}
                aria-label="Search"
                data-testid="mobile-search-toggle"
              >
                <SearchIcon className="w-5 h-5" />
              </button>
              <LanguageSwitcher />
              <button
                className="p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Search Bar */}
          {searchOpen && (
            <div className="lg:hidden py-3 border-t px-2">
              <HeaderSearch onClose={() => setSearchOpen(false)} />
            </div>
          )}

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t">
              <nav className="flex flex-col gap-2">
                {navLinks.map((link) =>
                  link.children ? (
                    <div key={link.label} className="space-y-2">
                      <span className="px-4 py-2 font-semibold text-[#0F172A]">{link.label}</span>
                      {link.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className="block px-8 py-2 text-slate-600 hover:text-[#0047AB]"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`px-4 py-2 font-medium ${
                        isActive(link.path) ? 'text-[#0047AB]' : 'text-[#0F172A]'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  )
                )}
                <Link
                  to="/rfq"
                  className="mx-4 mt-4"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Button className="w-full bg-[#D32F2F] hover:bg-[#B71C1C] text-white font-bold uppercase">
                    {c.cta?.primaryButton || 'Request Quote'}
                  </Button>
                </Link>
              </nav>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

const Footer = ({ siteContent }) => {
  const currentYear = new Date().getFullYear();
  const c = siteContent || DEFAULT_CONTENT;
  
  const companyName = c.brand?.companyName || DEFAULT_CONTENT.brand.companyName;
  const copyrightText = c.brand?.copyrightText || DEFAULT_CONTENT.brand.copyrightText;
  const footer = c.footer || DEFAULT_CONTENT.footer;
  const contact = c.contact || DEFAULT_CONTENT.contact;
  const whatsappNumber = contact.whatsapp || DEFAULT_CONTENT.contact.whatsapp;

  return (
    <footer className="bg-[#0F172A] text-slate-300">
      {/* Main Footer */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-16 h-16 flex-shrink-0 bg-white rounded-sm p-1 flex items-center justify-center">
                <img src={LOGO_URL} alt="Singhania International Ltd." className="h-14 w-auto object-contain" width="56" height="56" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg text-white leading-tight">{companyName}</h3>
                <span className="text-xs text-[#D32F2F] font-semibold">{footer.starExportHouse}</span>
              </div>
            </div>
            <p className="text-sm leading-relaxed mb-4">
              {footer.companyDescription}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-heading text-xl mb-6">{footer.quickLinksTitle}</h4>
            <ul className="space-y-3">
              <li><Link to="/about" className="hover:text-[#D32F2F] transition-colors">About Us</Link></li>
              <li><Link to="/products" className="hover:text-[#D32F2F] transition-colors">Products</Link></li>
              <li><Link to="/trading" className="hover:text-[#D32F2F] transition-colors">Trading Division</Link></li>
              <li><Link to="/manufacturing" className="hover:text-[#D32F2F] transition-colors">Manufacturing Division</Link></li>
              <li><Link to="/blog" className="hover:text-[#D32F2F] transition-colors">Resources</Link></li>
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-white font-heading text-xl mb-6">{footer.productsTitle}</h4>
            <ul className="space-y-3">
              <li><Link to="/products?category=Steel Wires" className="hover:text-[#D32F2F] transition-colors">Steel Wires</Link></li>
              <li><Link to="/products?category=Fasteners+%26+Fixings" className="hover:text-[#D32F2F] transition-colors">Fasteners & Fixings</Link></li>
              <li><Link to="/products?category=Ferro Alloys" className="hover:text-[#D32F2F] transition-colors">Ferro Alloys</Link></li>
              <li><Link to="/products?category=Metals" className="hover:text-[#D32F2F] transition-colors">Metals</Link></li>
              <li><Link to="/products?category=Industrial Chemicals" className="hover:text-[#D32F2F] transition-colors">Industrial Chemicals</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-heading text-xl mb-6">{footer.contactTitle}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#D32F2F] flex-shrink-0 mt-1" />
                <span>{contact.address}</span>
              </li>
              <li>
                <a href={`tel:${contact.phone}`} className="flex items-center gap-3 hover:text-[#D32F2F] transition-colors">
                  <Phone className="w-5 h-5 text-[#D32F2F]" />
                  {contact.phone}
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-[#D32F2F] flex-shrink-0 mt-1" />
                  <div className="space-y-1">
                    <a href={`mailto:${contact.email}`} className="block hover:text-[#D32F2F] transition-colors">
                      {contact.email}
                    </a>
                    <a href="mailto:sales@singhaniainternational.com" className="block hover:text-[#D32F2F] transition-colors text-slate-400">
                      sales@singhaniainternational.com
                    </a>
                  </div>
                </div>
              </li>
            </ul>
            {/* WhatsApp Button */}
            <a 
              href={`https://wa.me/${whatsappNumber}?text=Hello,%20I%20am%20interested%20in%20your%20products.`}
              target="_blank" 
              rel="noopener noreferrer"
              className="mt-4 flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-sm transition-colors"
              data-testid="footer-whatsapp-btn"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              {footer.whatsappButton}
            </a>
          </div>
        </div>
      </div>

      {/* Certifications Bar */}
      <div className="border-t border-slate-700">
        <div className="container-custom py-6">
          <div className="flex flex-wrap justify-center gap-8 text-sm text-slate-400">
            {(footer.certifications || DEFAULT_CONTENT.footer.certifications).map((cert) => (
              <span key={cert} className="flex items-center gap-2">
                <span className="w-2 h-2 bg-[#D32F2F] rounded-full"></span>
                {cert}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-slate-700">
        <div className="container-custom py-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
            <p>&copy; {currentYear} {copyrightText}</p>
            <div className="flex gap-6">
              <Link to="/privacy" className="hover:text-[#D32F2F] transition-colors">{footer.privacyPolicy}</Link>
              <Link to="/terms" className="hover:text-[#D32F2F] transition-colors">{footer.termsOfService}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Sticky Mobile RFQ Button
const StickyRFQButton = ({ siteContent }) => {
  const c = siteContent || DEFAULT_CONTENT;
  return (
    <div className="sticky-rfq-mobile bg-[#D32F2F] p-3">
      <Link to="/rfq" className="flex items-center justify-center gap-2 text-white font-bold uppercase" data-testid="sticky-rfq-btn">
        <span>{c.cta?.primaryButton || 'Request a Quote'}</span>
        <ArrowRight className="w-5 h-5" />
      </Link>
    </div>
  );
};

// Floating Request Callback Button
const FloatingCallbackButton = () => {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const normalized = phone.replace(/[\s()-]/g, '');
    if (!/^\+[1-9]\d{7,14}$/.test(normalized)) {
      setPhoneError('Enter a valid international number with country code, for example +919876543210.');
      return;
    }
    setPhoneError('');
    setSubmitting(true);
    try {
      await publicAPI.submitCallback({ phone: normalized, website: '' });
      setSuccess(true);
      setPhone('');
      toast.success('We will call you back shortly!');
      setTimeout(() => { setOpen(false); setSuccess(false); }, 2500);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit. Please try again.';
      setPhoneError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => { setOpen(!open); setSuccess(false); setPhoneError(''); }}
        className="fixed bottom-24 right-6 z-50 w-14 h-14 bg-[#0047AB] hover:bg-[#003380] text-white rounded-full shadow-xl flex items-center justify-center transition-all hover:scale-110 md:bottom-24"
        data-testid="callback-fab"
        aria-label="Request a callback"
      >
        <Phone className="w-6 h-6" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#D32F2F] rounded-full animate-pulse" />
      </button>

      {/* Popup */}
      {open && (
        <div className="fixed bottom-[7.5rem] right-6 z-50 w-80 bg-white rounded-lg shadow-2xl border border-slate-200 overflow-hidden md:bottom-24" data-testid="callback-popup">
          <div className="bg-[#0047AB] px-5 py-4 text-white">
            <h3 className="font-heading font-bold text-base">Request a Callback</h3>
            <p className="text-blue-100 text-xs mt-1">Enter your number, we'll call you back.</p>
          </div>
          {success ? (
            <div className="px-5 py-8 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Phone className="w-6 h-6 text-green-600" />
              </div>
              <p className="font-semibold text-[#0F172A]">Thank you!</p>
              <p className="text-sm text-slate-500 mt-1">Our team will reach out shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-5 space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Phone Number *</label>
                <input
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+91 98765 43210"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0047AB] focus:border-transparent"
                  data-testid="callback-phone-input"
                />
                <input type="text" name="website" tabIndex="-1" autoComplete="off" className="hidden" aria-hidden="true" data-testid="callback-honeypot-input" />
                {phoneError && <p className="text-xs text-red-600 mt-1" data-testid="callback-phone-error">{phoneError}</p>}
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-2.5 bg-[#D32F2F] hover:bg-[#B71C1C] text-white font-bold text-sm rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="callback-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                {submitting ? 'Submitting...' : 'Call Me Back'}
              </button>
              <p className="text-[11px] text-slate-400 text-center">We respect your privacy. No spam, ever.</p>
            </form>
          )}
        </div>
      )}
    </>
  );
};

// Floating WhatsApp Button
const FloatingWhatsApp = () => (
  <a
    href="https://wa.me/919872581418"
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-6 right-6 z-[9999] w-14 h-14 md:w-14 md:h-14 bg-[#25D366] hover:bg-[#1ebe57] text-white rounded-full shadow-xl flex items-center justify-center transition-all hover:scale-110 group"
    aria-label="Chat with us on WhatsApp"
    data-testid="whatsapp-fab"
  >
    <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
    <span className="absolute right-full mr-3 px-3 py-1.5 bg-white text-slate-700 text-sm font-medium rounded-lg shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">Chat with us on WhatsApp</span>
  </a>
);

const PublicLayout = () => {
  const [siteContent, setSiteContent] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await publicAPI.getContent();
        setSiteContent(response.data);
      } catch (error) {
        console.error('Error fetching site content:', error);
      }
    };
    fetchContent();
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header siteContent={siteContent} />
      <main className="flex-1">
        <Outlet context={{ siteContent }} />
      </main>
      <Footer siteContent={siteContent} />
      <FloatingWhatsApp />
      <FloatingCallbackButton />
      <StickyRFQButton siteContent={siteContent} />
    </div>
  );
};

export default PublicLayout;
